import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Play, VideoOff, X } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { ResponsiveModal } from "../components/ResponsiveModal";
import { VIDEO_GALLERY } from "../data/video-gallery";

function VideoCard({ item, index, onOpen, reduceMotion }) {
  const [posterFailed, setPosterFailed] = useState(false);

  return (
    <motion.article
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-black/5 bg-white shadow-soft transition-transform duration-300 hover:-translate-y-1 hover:shadow-soft-lg motion-reduce:transform-none motion-reduce:transition-none"
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      whileInView={reduceMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: Math.min((index % 3) * 0.05, 0.1), ease: [0.22, 1, 0.36, 1] }}
    >
      <button
        type="button"
        className="relative block aspect-[4/5] w-full overflow-hidden bg-neutral-100 text-left focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-orange/40"
        onClick={() => onOpen(item)}
        aria-label={`Watch ${item.title}`}
        aria-haspopup="dialog"
        data-testid={`gallery-card-${item.id}`}
      >
        {!posterFailed ? (
          <img
            src={item.posterSrc}
            alt={`${item.title} — S-UK Driving School learner`}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.025] motion-reduce:transform-none motion-reduce:transition-none"
            width="720"
            height="900"
            loading="lazy"
            decoding="async"
            draggable="false"
            onError={() => setPosterFailed(true)}
          />
        ) : (
          <span className="brand-gradient flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center text-white" role="status">
            <VideoOff size={30} aria-hidden="true" />
            <span className="text-sm font-semibold">Video preview unavailable</span>
          </span>
        )}

        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/55 via-black/15 to-transparent" aria-hidden="true" />
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
          <span className="brand-gradient flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/90 text-white shadow-[0_12px_30px_rgba(0,0,0,0.28)] transition-transform duration-300 group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none">
            <Play size={27} className="ml-1 fill-white" />
          </span>
        </span>
        <span className="pointer-events-none absolute bottom-4 left-4 rounded-full bg-black/45 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-sm" aria-hidden="true">
          Watch video
        </span>
      </button>

      <div className="flex flex-1 flex-col px-5 py-4">
        <h3 className="font-heading text-base font-bold text-neutral-900">{item.title}</h3>
        <p className="mt-1 text-xs text-neutral-500">{item.description}</p>
      </div>
    </motion.article>
  );
}

function VideoModal({ item, onClose }) {
  const videoRef = useRef(null);
  const [failed, setFailed] = useState(false);

  const close = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
      video.removeAttribute("src");
      video.load();
    }
    onClose();
  }, [onClose]);

  return (
    <ResponsiveModal
      title={item.title}
      description={item.description}
      onClose={close}
      className="!w-[min(100%,64rem)] !overflow-hidden !rounded-2xl !bg-neutral-950"
      testId="video-gallery-modal"
    >
      <div className="relative flex w-full flex-col bg-neutral-950 text-white">
        <button
          type="button"
          className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/70 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          onClick={close}
          aria-label="Close video"
          data-modal-initial-focus
          data-testid="video-modal-close"
        >
          <X size={23} aria-hidden="true" />
        </button>

        <div className="flex min-h-0 flex-1 items-center justify-center bg-black">
          {!failed ? (
            <video
              ref={videoRef}
              src={item.videoSrc}
              poster={item.posterSrc}
              className="max-h-[calc(100dvh-10rem)] w-full bg-black object-contain sm:max-h-[calc(100dvh-12rem)]"
              controls
              autoPlay
              playsInline
              preload="metadata"
              aria-label={`${item.title} video`}
              onError={() => setFailed(true)}
              data-testid="gallery-modal-video"
            >
              Your browser does not support HTML5 video.
            </video>
          ) : (
            <div className="flex min-h-72 w-full flex-col items-center justify-center gap-3 px-6 text-center">
              <VideoOff size={34} className="text-brand-orange" aria-hidden="true" />
              <p className="text-sm">This video could not be played in your browser.</p>
            </div>
          )}
        </div>

        <div className="border-t border-white/10 px-5 py-4 pr-16 sm:px-6">
          <h3 className="font-heading text-lg font-bold">{item.title}</h3>
          <p className="mt-1 text-sm text-white/65">{item.description}</p>
        </div>
      </div>
    </ResponsiveModal>
  );
}

export function Gallery() {
  const reduceMotion = useReducedMotion();
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    loop: false,
    duration: reduceMotion ? 12 : 24,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(1);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  const syncCarouselState = useCallback((api) => {
    setSelectedIndex(api.selectedScrollSnap());
    setSnapCount(Math.max(1, api.scrollSnapList().length));
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return undefined;
    syncCarouselState(emblaApi);
    emblaApi.on("select", syncCarouselState);
    emblaApi.on("reInit", syncCarouselState);
    return () => {
      emblaApi.off("select", syncCarouselState);
      emblaApi.off("reInit", syncCarouselState);
    };
  }, [emblaApi, syncCarouselState]);

  const closeVideo = useCallback(() => setActiveVideo(null), []);

  const handleCarouselKeyDown = (event) => {
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      emblaApi?.scrollPrev();
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      emblaApi?.scrollNext();
    }
  };

  const progress = `${Math.min(100, ((selectedIndex + 1) / snapCount) * 100)}%`;

  return (
    <section id="gallery" className="section-pad overflow-hidden rounded-2xl bg-neutral-50" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <span className="overline">Video Gallery</span>
          <h2 className="mt-3 max-w-2xl font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">
            Real learners, real passes &amp; <span className="brand-text">proud moments</span>
          </h2>
          <p className="mt-4 max-w-2xl text-neutral-500">
            Watch genuine S-UK learner success stories and pass-day celebrations.
          </p>
        </Reveal>

        <div
          className="mt-10 outline-none focus-visible:rounded-3xl focus-visible:ring-4 focus-visible:ring-brand-orange/25"
          role="region"
          aria-roledescription="carousel"
          aria-label="Learner success video gallery"
          tabIndex="0"
          onKeyDown={handleCarouselKeyDown}
          data-testid="video-carousel"
        >
          <div ref={emblaRef} className="overflow-hidden rounded-2xl">
            <div className="-ml-4 flex touch-pan-y">
              {VIDEO_GALLERY.map((item, index) => (
                <div
                  key={item.id}
                  className="min-w-0 shrink-0 grow-0 basis-full pl-4 md:basis-1/2 lg:basis-1/3"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${VIDEO_GALLERY.length}`}
                >
                  <VideoCard item={item} index={index} onOpen={setActiveVideo} reduceMotion={reduceMotion} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-7 flex items-center justify-between gap-4">
            <button
              type="button"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-900 shadow-soft transition-all hover:-translate-y-0.5 hover:border-brand-orange hover:text-brand-red disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:translate-y-0 motion-reduce:transform-none motion-reduce:transition-none"
              onClick={() => emblaApi?.scrollPrev()}
              disabled={!canScrollPrev}
              aria-label="Previous videos"
              data-testid="gallery-prev"
            >
              <ChevronLeft size={24} aria-hidden="true" />
            </button>

            <div className="flex min-w-0 flex-1 items-center justify-center gap-3" aria-live="polite">
              <div className="h-1.5 w-full max-w-56 overflow-hidden rounded-full bg-neutral-200" aria-hidden="true">
                <div
                  className="brand-gradient h-full rounded-full transition-[width] duration-300 motion-reduce:transition-none"
                  style={{ width: progress }}
                />
              </div>
              <span className="shrink-0 text-xs font-semibold tabular-nums text-neutral-500">
                {selectedIndex + 1} / {snapCount}
              </span>
            </div>

            <button
              type="button"
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-900 shadow-soft transition-all hover:-translate-y-0.5 hover:border-brand-orange hover:text-brand-red disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:translate-y-0 motion-reduce:transform-none motion-reduce:transition-none"
              onClick={() => emblaApi?.scrollNext()}
              disabled={!canScrollNext}
              aria-label="Next videos"
              data-testid="gallery-next"
            >
              <ChevronRight size={24} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {activeVideo && <VideoModal key={activeVideo.id} item={activeVideo} onClose={closeVideo} />}
      </AnimatePresence>
    </section>
  );
}
